import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { registerToastHandler } from "./toastBridge";

type ToastKind = "success" | "error" | "info";

type ToastAction = {
  label: string;
  onClick: () => void;
};

type ToastItem = {
  id: string;
  kind: ToastKind;
  title: string;
  description?: string;
  /** null = persists until dismissed (default for errors). */
  durationMs: number | null;
  onClick?: () => void;
  action?: ToastAction;
  leaving?: boolean;
};

type ToastOptions = {
  /** Force an auto-dismiss duration. Errors persist by default; pass this to
   *  make a specific error auto-dismiss too. */
  durationMs?: number;
  onClick?: () => void;
  /** Inline action button (e.g. Retry / View). Fires then dismisses the toast. */
  action?: ToastAction;
};

type ToastContextValue = {
  success: (title: string, description?: string, options?: ToastOptions) => void;
  error: (title: string, description?: string, options?: ToastOptions) => void;
  info: (title: string, description?: string, options?: ToastOptions) => void;
};

/** Public shape of the toast API — shared by the hook and the imperative bridge. */
export type ToastApi = ToastContextValue;
export type { ToastOptions, ToastAction };

const ToastContext = createContext<ToastContextValue | null>(null);

// Per-variant auto-dismiss timings (handoff §Variants).
const SUCCESS_DURATION_MS = 3000;
const SUCCESS_ACTION_DURATION_MS = 5000;
const INFO_DURATION_MS = 4000;
// Time the card is kept mounted while its exit animation plays.
const LEAVE_MS = 200;
// Only the newest N toasts are visible on desktop (handoff §Placement).
const MAX_VISIBLE = 3;

const createToastId = () =>
  `toast_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

function resolveDuration(
  kind: ToastKind,
  options: ToastOptions | undefined
): number | null {
  if (options?.durationMs != null) return options.durationMs;
  if (kind === "error") return null; // errors persist until dismissed
  if (kind === "info") return INFO_DURATION_MS;
  return options?.action ? SUCCESS_ACTION_DURATION_MS : SUCCESS_DURATION_MS;
}

const DISC: Record<
  ToastKind,
  { bg: string; ink: string; glyph: string; label: string }
> = {
  success: { bg: "#2BA36B", ink: "#FBF9F5", glyph: "✓", label: "Success" },
  error: { bg: "#D64545", ink: "#FBF9F5", glyph: "!", label: "Error" },
  info: { bg: "#F2A93B", ink: "#141B2D", glyph: "i", label: "Information" },
};

export type ToastProviderProps = {
  children: React.ReactNode;
  closeLabel?: string;
};

export function ToastProvider({ children, closeLabel = "Close" }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const leaveTimersRef = useRef(new Map<string, number>());

  const remove = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = leaveTimersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      leaveTimersRef.current.delete(id);
    }
  }, []);

  // Play the exit animation, then unmount.
  const dismiss = useCallback(
    (id: string) => {
      if (leaveTimersRef.current.has(id)) return; // already leaving
      setToasts((current) =>
        current.map((toast) =>
          toast.id === id ? { ...toast, leaving: true } : toast
        )
      );
      const timer = window.setTimeout(() => remove(id), LEAVE_MS);
      leaveTimersRef.current.set(id, timer);
    },
    [remove]
  );

  const enqueue = useCallback(
    (kind: ToastKind, title: string, description?: string, options?: ToastOptions) => {
      const id = createToastId();
      setToasts((current) => [
        ...current,
        {
          id,
          kind,
          title,
          description,
          durationMs: resolveDuration(kind, options),
          onClick: options?.onClick,
          action: options?.action,
        },
      ]);
    },
    []
  );

  useEffect(() => {
    const timers = leaveTimersRef.current;
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const value = useMemo(
    () => ({
      success: (title: string, description?: string, options?: ToastOptions) =>
        enqueue("success", title, description, options),
      error: (title: string, description?: string, options?: ToastOptions) =>
        enqueue("error", title, description, options),
      info: (title: string, description?: string, options?: ToastOptions) =>
        enqueue("info", title, description, options),
    }),
    [enqueue]
  );

  // Expose the API imperatively so non-React callers (e.g. the React Query
  // client) can raise toasts. Registered while mounted, cleared on unmount.
  useEffect(() => {
    registerToastHandler(value);
    return () => registerToastHandler(null);
  }, [value]);

  // Newest last: desktop stacks upward (newest at the bottom edge), older ones
  // beyond MAX_VISIBLE collapse. Mobile shows only the newest (CSS below).
  const visible = toasts.slice(-MAX_VISIBLE);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <style>{TOAST_KEYFRAMES}</style>
      <div
        className="a2z-toast-layer pointer-events-none fixed z-50 flex flex-col gap-3"
        aria-live="polite"
        style={{ fontFamily: "var(--font-body, system-ui, sans-serif)" }}
      >
        {visible.map((toast) => (
          <ToastCard
            key={toast.id}
            toast={toast}
            closeLabel={closeLabel}
            onDismiss={() => dismiss(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

type ToastCardProps = {
  toast: ToastItem;
  closeLabel: string;
  onDismiss: () => void;
};

function ToastCard({ toast, closeLabel, onDismiss }: ToastCardProps) {
  const { kind, durationMs, action, description } = toast;
  const disc = DISC[kind];
  const isError = kind === "error";
  const autoDismisses = durationMs != null;

  const [paused, setPaused] = useState(false);
  // Remaining time survives pause/resume cycles (hover pause).
  const remainingRef = useRef<number>(durationMs ?? 0);

  useEffect(() => {
    if (durationMs == null || paused || toast.leaving) return;
    const startedAt = Date.now();
    const timer = window.setTimeout(onDismiss, remainingRef.current);
    return () => {
      window.clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [durationMs, paused, toast.leaving, onDismiss]);

  const dismissOnBodyClick = !action && !isError;

  return (
    <div
      className="a2z-toast-card pointer-events-auto box-border overflow-hidden"
      role={isError ? "alert" : "status"}
      data-leaving={toast.leaving ? "" : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={
        dismissOnBodyClick
          ? () => {
              toast.onClick?.();
              onDismiss();
            }
          : undefined
      }
      style={{
        background: "#141B2D",
        borderRadius: 12,
        boxShadow: "0 12px 32px rgba(15,20,35,0.25)",
        cursor: dismissOnBodyClick ? "pointer" : "default",
      }}
    >
      <div
        className="flex gap-3 px-4 py-3.5"
        style={{ alignItems: description ? "flex-start" : "center" }}
      >
        <span
          className="a2z-toast-disc flex flex-shrink-0 items-center justify-center rounded-full"
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            background: disc.bg,
            color: disc.ink,
            fontSize: 13,
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {disc.glyph}
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span
            style={{ fontSize: 14, fontWeight: 600, color: "#FBF9F5" }}
          >
            {toast.title}
          </span>
          {description ? (
            <span style={{ fontSize: 13, color: "#B9BFCC" }}>{description}</span>
          ) : null}
        </div>

        {action ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              action.onClick();
              onDismiss();
            }}
            className="flex-shrink-0 bg-transparent p-0"
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: "#F2A93B",
              cursor: "pointer",
            }}
          >
            {action.label}
          </button>
        ) : null}

        {isError ? (
          <button
            type="button"
            aria-label={closeLabel}
            onClick={(event) => {
              event.stopPropagation();
              onDismiss();
            }}
            className="a2z-toast-close flex flex-shrink-0 items-center bg-transparent p-0"
            style={{ color: "rgba(251,249,245,0.5)", cursor: "pointer" }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        ) : null}
      </div>

      {autoDismisses ? (
        <div style={{ height: 3, background: "rgba(251,249,245,0.12)" }}>
          <span
            className="a2z-toast-timer block h-full"
            style={{
              background: disc.bg,
              animationName: "a2zToastTimer",
              animationDuration: `${durationMs}ms`,
              animationTimingFunction: "linear",
              animationFillMode: "both",
              animationPlayState: paused ? "paused" : "running",
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

// Injected once. Keyframes + placement live here because @a2z/ui ships no CSS
// file; keeping them scoped by the a2z-toast-* class names avoids collisions.
const TOAST_KEYFRAMES = `
@keyframes a2zToastIn { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes a2zToastInTop { from { transform: translateY(-12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes a2zToastOut { from { opacity: 1; } to { transform: translateY(12px); opacity: 0; } }
@keyframes a2zToastOutTop { from { opacity: 1; } to { transform: translateY(-12px); opacity: 0; } }
@keyframes a2zToastFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes a2zToastFadeOut { from { opacity: 1; } to { opacity: 0; } }
@keyframes a2zToastDiscPop { 0% { transform: scale(0); } 70% { transform: scale(1.15); } 100% { transform: scale(1); } }
@keyframes a2zToastTimer { from { width: 100%; } to { width: 0%; } }

/* Mobile default: top-center, below the safe area, full width minus 16px. */
.a2z-toast-layer {
  inset-block-start: calc(env(safe-area-inset-top, 0px) + 12px);
  inset-inline: 16px;
  align-items: stretch;
}
/* Mobile shows only the newest (new toast replaces the current one). */
.a2z-toast-layer > .a2z-toast-card:not(:last-child) { display: none; }

.a2z-toast-card {
  width: 100%;
  max-width: 380px;
  margin-inline: auto;
  animation: a2zToastInTop 0.3s cubic-bezier(0.16,1,0.3,1) both;
}
.a2z-toast-card[data-leaving] { animation: a2zToastOutTop 0.2s ease-in both; }
.a2z-toast-disc { animation: a2zToastDiscPop 0.35s cubic-bezier(0.34,1.56,0.64,1) 0.15s both; }
.a2z-toast-close:hover { color: rgba(251,249,245,0.85) !important; }

/* Desktop: bottom-left (mirrors to bottom-right in RTL), stacks upward. */
@media (min-width: 640px) {
  .a2z-toast-layer {
    inset-block: auto 24px;
    inset-inline: 24px auto;
    width: 360px;
    align-items: flex-start;
  }
  .a2z-toast-layer > .a2z-toast-card:not(:last-child) { display: block; }
  .a2z-toast-card { margin-inline: 0; animation: a2zToastIn 0.3s cubic-bezier(0.16,1,0.3,1) both; }
  .a2z-toast-card[data-leaving] { animation: a2zToastOut 0.2s ease-in both; }
}

/* Reduced motion: opacity-only entrance/exit; keep the timer bar draining. */
@media (prefers-reduced-motion: reduce) {
  .a2z-toast-card,
  .a2z-toast-card[data-leaving] { animation-name: a2zToastFade; }
  .a2z-toast-card[data-leaving] { animation-name: a2zToastFadeOut; }
  .a2z-toast-disc { animation: none; }
}
`;

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}
